import { useMemo, useState } from 'react';
import { showErrorSnackbar, showSuccessSnackbar } from '../../../../components/snackBar/snakbar';
import { formatUnixTimestamp } from '../../../../library/dayJs';
import { CONFIRMATION_TEXT } from '../data/accountDeletionData';
import {
  useCancelAccountDeletionMutation,
  useGetAccountDeletionQuery,
  useRequestAccountDeletionMutation,
} from '../api/accountDeletion.api';

function getErrorMessage(error: unknown, fallback: string): string {
  if (error && typeof error === 'object' && 'message' in error) {
    const message = (error as { message?: string }).message;
    if (message) return message;
  }
  return fallback;
}

export function useDeleteAccount() {
  const [description, setDescription] = useState('');
  const [confirmationInput, setConfirmationInput] = useState('');
  const [acknowledged, setAcknowledged] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const { data: pendingRequest, isLoading: isLoadingPending } = useGetAccountDeletionQuery();
  const [requestAccountDeletion, { isLoading: isRequesting }] = useRequestAccountDeletionMutation();
  const [cancelAccountDeletion, { isLoading: isCancelling }] = useCancelAccountDeletionMutation();

  const isConfirmationValid = confirmationInput.trim().toLowerCase() === CONFIRMATION_TEXT;
  const showPendingState = Boolean(pendingRequest);
  const canDelete = acknowledged && !showPendingState;
  const canSubmitModal = isConfirmationValid && !isRequesting;

  const cancellableUntilLabel = useMemo(
    () =>
      pendingRequest?.cancellableUntil
        ? formatUnixTimestamp(pendingRequest.cancellableUntil, 'DD-MMM-YYYY HH:mm')
        : '',
    [pendingRequest?.cancellableUntil],
  );

  const resetForm = () => {
    setDescription('');
    setConfirmationInput('');
    setAcknowledged(false);
  };

  const openDeleteModal = () => {
    setDescription('');
    setConfirmationInput('');
    setIsDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    if (isRequesting) return;
    setDescription('');
    setConfirmationInput('');
    setIsDeleteModalOpen(false);
  };

  const handleRequestDeletion = async () => {
    try {
      await requestAccountDeletion({
        description: description.trim() || undefined,
      }).unwrap();
      setIsDeleteModalOpen(false);
      resetForm();
      showSuccessSnackbar('Account deletion requested. You have 7 days to cancel this request.');
    } catch (error) {
      showErrorSnackbar(getErrorMessage(error, 'Failed to request account deletion'));
    }
  };

  const handleCancelDeletion = async () => {
    try {
      await cancelAccountDeletion().unwrap();
      resetForm();
      showSuccessSnackbar('Account deletion request cancelled successfully.');
    } catch (error) {
      showErrorSnackbar(getErrorMessage(error, 'Failed to cancel account deletion request'));
    }
  };

  return {
    description,
    setDescription,
    confirmationInput,
    setConfirmationInput,
    acknowledged,
    setAcknowledged,
    pendingRequest,
    isLoadingPending,
    isRequesting,
    isCancelling,
    isConfirmationValid,
    showPendingState,
    canDelete,
    canSubmitModal,
    cancellableUntilLabel,
    isDeleteModalOpen,
    openDeleteModal,
    closeDeleteModal,
    handleRequestDeletion,
    handleCancelDeletion,
  };
}
